import React, { useEffect } from 'react'
import { Button, Card, Col, Form, Input, Row, Space, Table, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAdd, faClose, faRefresh } from '@fortawesome/free-solid-svg-icons'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import MedicalAidPaymentForm from '../../medical-aid-payment-form'
import { useRemittanceForm } from '../hooks/useRemittanceForm'

/**
 * Component for displaying and managing remittance details
 * @param {Object} data - Remittance data
 * @param {Function} handleGetRemittances - Function to refresh remittances list
 */
const RemittanceForm = ({ data, handleGetRemittances }) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { token } = theme.useToken()

  // Initialize the remittance form hook
  const { loading, remittanceData, handleRefresh, handleCloseTab } = useRemittanceForm(data, form, handleGetRemittances)

  // Fetch data on mount
  useEffect(() => {
    handleRefresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Opens a new medical aid payment form in a new tab
   */
  const handleNewPayment = () => {
    dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.PAYMENT_REMITTANCE}_new`,
        closable: true,
        label: 'Create New Medical Aid Payment',
        children: <MedicalAidPaymentForm data={data} handleGetRemittances={handleGetRemittances} />
      })
    )
  }

  // Button style for consistency
  const buttonStyle = {
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 16px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  }

  return (
    <Row>
      <Col span={24}>
        <Card
          type='inner'
          title={
            <h2 style={{ color: token.colorPrimary }}>
              Remittance: {remittanceData.medicalAid} - {remittanceData.receiptNumber}
            </h2>
          }
          extra={
            <Space>
              <Button
                style={{
                  ...buttonStyle,
                  background: '#e6f7ff',
                  borderColor: '#91d5ff',
                  color: '#1890ff'
                }}
                onClick={handleNewPayment}
                icon={<FontAwesomeIcon icon={faAdd} style={{ color: '#1890ff' }} />}
              >
                New Medical Aid Payment
              </Button>
              <Button
                style={{
                  ...buttonStyle,
                  background: '#f6ffed',
                  borderColor: '#b7eb8f',
                  color: '#52c41a'
                }}
                onClick={handleRefresh}
                icon={<FontAwesomeIcon icon={faRefresh} style={{ color: '#52c41a' }} />}
              >
                Refresh
              </Button>
            </Space>
          }
        >
          <Form
            initialValues={{
              ...remittanceData,
              remittanceDate: remittanceData.remittanceDate ? remittanceData.remittanceDate : null
            }}
            layout='vertical'
            form={form}
            disabled={loading}
          >
            <Row justify='space-between' gutter={[12, 12]}>
              <Col span={6}>
                <Form.Item name='total' label='Total'>
                  <Input readOnly />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='runningTotal' label='Running Total'>
                  <Input readOnly />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='practiceRefNo' label='Practice Reference Number'>
                  <Input readOnly />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name='remittanceDate' label='Remittance Date'>
                  <Input readOnly />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Row style={{ marginTop: 10 }}>
            <Col span={24}>
              <Card
                size='small'
                loading={loading}
                title={<h2 style={{ color: token.colorSecondary }}>Remittance Payments</h2>}
              >
                <Table
                  size='small'
                  pagination={false}
                  bordered
                  columns={[
                    {
                      title: 'Account',
                      dataIndex: ['patientRecord', 'idNo'],
                      key: 'account',
                      render: (text) => text || '-'
                    },
                    {
                      title: 'Patient',
                      key: 'patient',
                      render: (text, record) => {
                        if (!record?.patientRecord) return '-'
                        const firstName = record.patientRecord.firstName || ''
                        const lastName = record.patientRecord.lastName || ''
                        return firstName || lastName ? `${firstName} ${lastName}`.trim() : '-'
                      }
                    },
                    {
                      title: 'Invoice Number',
                      dataIndex: ['invoiceRecord', 'invoiceNo'],
                      key: 'invoiceNo',
                      render: (text, record) => {
                        if (!record.invoiceRef || record.invoiceRef === '') return '-'
                        return text || '-'
                      }
                    },
                    {
                      title: 'Invoice Total',
                      key: 'invoiceTotal',
                      render: (text, record) => {
                        if (!record.invoiceRef || record.invoiceRef === '') return 'R 0.00'
                        if (record.billingRecords && record.billingRecords.length > 0) {
                          return `R ${record.billingRecords
                            .reduce((acc, item) => parseFloat(acc) + parseFloat(item.total || 0), 0)
                            .toFixed(2)}`
                        }
                        return 'R 0.00'
                      }
                    },
                    {
                      title: 'Patient Due',
                      key: 'patientDue',
                      render: (text, record) => {
                        if (!record.invoiceRef || record.invoiceRef === '') return 'R 0.00'
                        if (record.billingRecords && record.billingRecords.length > 0) {
                          return `R ${record.billingRecords
                            .reduce((acc, item) => parseFloat(acc) + parseFloat(item.patient || 0), 0)
                            .toFixed(2)}`
                        }
                        return 'R 0.00'
                      }
                    },
                    {
                      title: 'Medical Aid Due',
                      key: 'medicalAidDue',
                      render: (text, record) => {
                        if (!record.invoiceRef || record.invoiceRef === '') return 'R 0.00'
                        if (record.billingRecords && record.billingRecords.length > 0) {
                          return `R ${record.billingRecords
                            .reduce((acc, item) => parseFloat(acc) + parseFloat(item.medicalAid || 0), 0)
                            .toFixed(2)}`
                        }
                        return 'R 0.00'
                      }
                    },
                    {
                      title: 'Remittance Payment',
                      key: 'remittancePayment',
                      render: (text, record) => {
                        return `R ${record.paymentAmount}`
                      }
                    }
                  ]}
                  dataSource={remittanceData.payments}
                />
              </Card>
            </Col>
          </Row>
          <Row justify='end' style={{ marginTop: 16 }}>
            <Col>
              <Button
                style={{
                  ...buttonStyle,
                  background: '#fff1f0',
                  borderColor: '#ffa39e',
                  color: '#f5222d'
                }}
                onClick={handleCloseTab}
                icon={<FontAwesomeIcon icon={faClose} style={{ color: '#f5222d' }} />}
              >
                Close
              </Button>
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  )
}

export default RemittanceForm
